import { LocalStackInstance, LocalStackInstanceStatusText, LocalStackInstanceType } from '@localstack/types';
import { ReactElement, useContext, useMemo } from 'react';
import {
  getSelectedInstance,
  GlobalStateContext,
  removeProtocolFromUrl,
  useAwsEffect,
  useDisableFeatures,
  useEndpoint,
  useGlobalSwr,
  useLocalstackStatus,
} from '@localstack/services';
import { INSTANCE_DEFAULT_ID } from '@localstack/constants';
import { Tooltip } from '@mui/material';
import type { UseAuthProvider, UseFeatureFlag } from '@localstack/types';

import { TopMenuSwitcher, TopMenuSwitcherProps } from '../TopMenuSwitcher';
import { BaseHeaderBreadcrumbs, InstanceActionsMenu } from '../../core';
import { InstanceStatusIcon } from '../../display';

import { BaseHeaderContainer } from './utils/styled-components';

type LocalStackInstanceHeaderProps = TopMenuSwitcherProps & {
  onSave?: (instance: LocalStackInstance) => void;
  onRemove?: (instance: LocalStackInstance) => void;
  isRemovingInstance?: boolean;
  useAuthProvider: UseAuthProvider;
  useFeatureFlags: UseFeatureFlag;
};

export const LocalStackInstanceHeader = ({
  instance,
  onRemove: onInstanceRemove,
  disabledRoutes,
  onSave,
  useAuthProvider,
  appInspectorNudge,
}: LocalStackInstanceHeaderProps): ReactElement => {
  const selectedInstance = getSelectedInstance();
  const { endpoint: globalEndpoint } = useEndpoint();
  const { clearAll: clearAllState } = useGlobalSwr();
  const { updateInstance } = useContext(GlobalStateContext);
  const onInstanceEdit = onSave ?? updateInstance;

  const isEphemeralInstance = instance?.instanceType === LocalStackInstanceType.EPHEMERAL_INSTANCE;
  const instanceEndpoint = selectedInstance?.endpoint || globalEndpoint;
  const isDefaultInstance = instance?.id === INSTANCE_DEFAULT_ID;
  const enableAction = useDisableFeatures({ endpoint: instance?.endpoint });

  const { running } = useLocalstackStatus({ endpoint: instance?.endpoint });
  const { restartInstance, isLoading: isInstanceRestarting } = useAwsEffect('LocalStack', ['restartInstance'], {
    clientOverrides: { endpoint: instanceEndpoint },
  });
  const deadline = useMemo(
    () => Math.trunc(((instance?.ephemeralInstance?.expiry_time ?? 0) - Date.now() / 1000) / 60),
    [instance],
  );

  const instanceUrlWithoutProtocol = removeProtocolFromUrl(instance?.endpoint ?? '');
  const localInstanceStatusText = running
    ? LocalStackInstanceStatusText.RUNNING
    : LocalStackInstanceStatusText.NOT_RUNNING;

  return (
    <div data-name="resource browser preface">
      <BaseHeaderContainer px={2} py={1} className="base-header-container">
        <BaseHeaderBreadcrumbs
          items={[
            {
              label: instanceUrlWithoutProtocol,
              icon: (
                <Tooltip title={isEphemeralInstance ? `${deadline} minutes remaining` : localInstanceStatusText}>
                  <span>
                    <InstanceStatusIcon isEphemeralInstance={isEphemeralInstance} isInstanceRunning={enableAction} />
                  </span>
                </Tooltip>
              ),
            },
          ]}
        />
        {instance && (
          <InstanceActionsMenu
            isDefaultInstance={isDefaultInstance}
            clearAllState={clearAllState}
            onInstanceRemove={onInstanceRemove}
            onInstanceEdit={onInstanceEdit}
            restartInstance={restartInstance}
            isInstanceRunning={enableAction}
            isEphemeralInstance={isEphemeralInstance}
            isInstanceRestarting={isInstanceRestarting}
            instance={instance}
          />
        )}
        <TopMenuSwitcher
          instance={instance}
          disabledRoutes={disabledRoutes}
          useAuthProvider={useAuthProvider}
          appInspectorNudge={appInspectorNudge}
        />
      </BaseHeaderContainer>
    </div>
  );
};
